import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/api';

const MyAccount: React.FC = () => {
  const { employee } = useAuth();
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordQuestion, setPasswordQuestion] = useState('');
  const [passwordAnswer, setPasswordAnswer] = useState('');

  useEffect(() => {
    if (employee) {
      setEmail(employee.email || '');
      setPasswordQuestion(employee.passwordQuestion || '');
      setPasswordAnswer(employee.passwordAnswer || '');
    }
  }, [employee]);

  const handleUpdateAccount = async () => {
    if (!email.trim()) {
      alert('Email is required');
      return;
    }

    if (newPassword && newPassword !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    if (newPassword && newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    try {
      await authApi.updateAccount({
        email: email.trim(),
        newPassword: newPassword || undefined,
        passwordQuestion: passwordQuestion.trim() || undefined,
        passwordAnswer: passwordAnswer.trim() || undefined,
      });

      alert('Account updated successfully!');
      setShowUpdateModal(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update account';
      alert('Error: ' + errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const maskPassword = () => {
    return '•'.repeat(8);
  };

  if (!employee) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-600">Loading account information...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">My Account</h1>

      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="space-y-4">
          <div className="flex justify-between items-center py-3 border-b border-gray-200">
            <span className="text-sm font-medium text-gray-600">Password</span>
            <span className="text-sm text-gray-900">{maskPassword()}</span>
          </div>

          <div className="flex justify-between items-center py-3 border-b border-gray-200">
            <span className="text-sm font-medium text-gray-600">Email Address</span>
            <span className="text-sm text-gray-900">{employee.email || 'Not set'}</span>
          </div>

          <div className="py-3 border-b border-gray-200">
            <span className="text-sm font-medium text-gray-600 block mb-2">Password Question</span>
            <span className="text-sm text-gray-900">{employee.passwordQuestion || 'Not set'}</span>
          </div>
        </div>
      </div>

      <button
        onClick={() => setShowUpdateModal(true)}
        className="w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        Update Account Information
      </button>

      {/* Update Account Modal */}
      {showUpdateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Update Account Information</h3>
              <button
                onClick={() => setShowUpdateModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            <div className="mt-4 space-y-4 max-h-96 overflow-y-auto">
              <div>
                <label htmlFor="update-email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  id="update-email"
                  type="email"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
                  New Password (Optional)
                </label>
                <input
                  id="new-password"
                  type="password"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Leave blank to keep current password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                  Confirm New Password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="password-question" className="block text-sm font-medium text-gray-700">
                  Password Question (Optional)
                </label>
                <input
                  id="password-question"
                  type="text"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter password recovery question"
                  value={passwordQuestion}
                  onChange={(e) => setPasswordQuestion(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="password-answer" className="block text-sm font-medium text-gray-700">
                  Password Answer (Optional)
                </label>
                <input
                  id="password-answer"
                  type="text"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter password recovery answer"
                  value={passwordAnswer}
                  onChange={(e) => setPasswordAnswer(e.target.value)}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="mt-6 flex gap-2">
              <button
                onClick={() => setShowUpdateModal(false)}
                disabled={isLoading}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateAccount}
                disabled={isLoading}
                className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAccount;


